/**
 * „Okładka" — the pool as a grid, plus the two acts the phone has always offered: add one of your
 * own, and throw one away.
 *
 * ── A GRID, not a row ───────────────────────────────────────────────────────────────────────
 * It was a horizontally scrolling row while the pool held four curated pictures. With up to ten of
 * the trainer's own beside them the row became a scroller whose far end nothing announced, so the
 * tiles wrap. The box reserves ONE row's height from the first frame, so the pending read and the
 * answered one start at the same geometry and only the tiles below it arrive.
 *
 * ── The tiles are still radios, and the trash is a BUTTON inside one ────────────────────────
 * One `radiogroup`, arrow keys between covers, exactly as before. The trash badge on an own tile is
 * a real button nested in the label, so it needs `stopPropagation` — a click on it is a request to
 * delete that picture, never a request to choose it.
 *
 * ── The add tile DIMS at the ceiling rather than disappearing ───────────────────────────────
 * Ten is the server's number (`PlanCoverProcessor.MaxOwnedCovers`), read off `ownLimit`. A control
 * that vanishes teaches nothing about why; the counter under the grid says what happened, and the
 * tile stays where the trainer last saw it. Its input is genuinely `disabled` — this is the one
 * control the view ADR's `aria-disabled` rule cannot cover, because a focusable file input opens
 * the operating system's chooser on Enter and there is no way to refuse the file afterwards
 * without the trainer having already picked one. The counter beside it carries the reason.
 */

import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog, type ConfirmPrompt } from '~/shared/ui/ConfirmDialog';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import { TrashGlyph } from '~/shared/ui/FormGlyphs';
import { COVER_ACCEPT } from '~/shared/media/coverFile';
import photoBox from '~/shared/ui/photoBox.module.css';
import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';

import { coverDeletePrompt } from '../model/coverPool';
import type { CreatePlanFlow } from '../lib/useCreatePlanFlow';

import styles from './CoverPicker.module.css';

export function CoverPicker({ flow }: { readonly flow: CreatePlanFlow }) {
  const { t } = useTranslation();
  const coverName = useId();
  const pool = flow.covers;
  /** The cover waiting on an answer, or `null` while nothing is being removed. */
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  /**
   * Where focus goes when the confirm closes: the trash badge that opened it is unmounted by the
   * time a successful delete lands, so Base UI's own restore would drop focus on `<body>`.
   */
  const addTile = useRef<HTMLLabelElement>(null);

  const prompt: ConfirmPrompt | null =
    pendingDelete === null
      ? null
      : { ...coverDeletePrompt(t), destructiveConfirm: true, busy: pool.busy };

  return (
    <fieldset className={styles.field}>
      <legend className={styles.label}>{t('web:planCreate.details.coverLabel')}</legend>

      <div className={styles.covers}>
        {pool.options.map((option, index) => {
          // The NUMBER a tile is called by, counted over the pictures alone: „auto" is named by its
          // own word, so counting it would leave the covers numbered 1, 3, 4 and the trash badge
          // naming a different cover than the tile it sits on.
          const number = pool.options
            .slice(0, index)
            .filter((earlier) => earlier.imageUrl !== null).length + 1;
          const selected = flow.choices.coverId === option.coverId;
          const key = option.coverId ?? 'auto';
          const removing = option.coverId !== null && pool.removingId === option.coverId;
          return (
            <label
              key={key}
              className={`${styles.cover} ${selected ? styles.coverOn : ''} ${removing ? styles.coverGoing : ''}`}
            >
              <input
                className={hiddenControl.control}
                type="radio"
                name={coverName}
                value={String(key)}
                checked={selected}
                // Every option needs a NAME: the label holds nothing but an image with an empty
                // `alt`, so without this the group read as unnamed radios and the arrows moved
                // between things a reader could not tell apart. The pool carries no name on the
                // wire (`PlanCoverResponse` is an id, a url and a flag), so the position in the
                // grid is what is left to say — which is also what a sighted trainer picks by.
                aria-label={
                  option.imageUrl === null
                    ? t('web:planCreate.details.coverAutoLabel')
                    : t('web:planCreate.details.coverOptionLabel', { n: number })
                }
                onChange={() => {
                  flow.setCover(option.coverId);
                }}
              />
              {option.imageUrl === null ? (
                <>
                  <img
                    className={`${String(photoBox.photo)} ${String(styles.coverImage)}`}
                    src={flow.autoCoverUrl}
                    alt=""
                    decoding="async"
                  />
                  <span className={styles.coverAuto}>{t('web:planCreate.details.coverAuto')}</span>
                </>
              ) : (
                <img
                  className={`${String(photoBox.photo)} ${String(styles.coverImage)}`}
                  src={option.imageUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
              )}
              {option.isOwn && option.coverId !== null ? (
                <button
                  type="button"
                  className={styles.trash}
                  aria-label={t('plans.builder.coverDeleteA11y', { number })}
                  aria-disabled={pool.busy}
                  onClick={(event) => {
                    // Nested in the label, so the click would otherwise also check the radio —
                    // the trainer would delete the picture AND select it on the way out.
                    event.preventDefault();
                    event.stopPropagation();
                    if (pool.busy) return;
                    setPendingDelete(option.coverId);
                  }}
                >
                  <TrashGlyph />
                </button>
              ) : null}
            </label>
          );
        })}

        {/* The add tile is a LABEL over a hidden file input — the pattern the account photo and the
          * exercise creator both use, and the only one that keeps a native file chooser reachable
          * from the keyboard. */}
        <label
          ref={addTile}
          className={`${styles.cover} ${styles.add} ${pool.canAdd ? '' : styles.addOff}`}
          aria-disabled={!pool.canAdd}
        >
          <input
            className={hiddenControl.control}
            type="file"
            accept={COVER_ACCEPT}
            aria-label={t('plans.builder.coverUploadA11y')}
            disabled={!pool.canAdd}
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared before anything async runs: without it, choosing the SAME file again
              // fires no change event and the second attempt does nothing at all.
              event.target.value = '';
              if (file !== undefined) flow.covers.add(file);
            }}
          />
          <span className={styles.addMark} aria-hidden="true">
            +
          </span>
        </label>
      </div>

      {/* The counter always stands, on every status, so nothing below it moves when the pool
        * answers. The two numbers are the SERVER's — this app never writes 10 down. */}
      <span className={styles.count}>
        {t('plans.builder.coverOwnCount', { count: pool.ownCount, limit: pool.ownLimit })}
      </span>

      <span className={styles.help}>{t('web:planCreate.details.coverNote')}</span>

      {/* The refusal line stands whether or not there is one to draw, so a failed upload changes
        * ink and never geometry — the rule the whole app loads under. */}
      <span className={styles.refusal}>{pool.error ?? ''}</span>

      <LiveRegionAnnouncer message={pool.announcement} />

      <ConfirmDialog
        prompt={prompt}
        finalFocus={addTile}
        onConfirm={() => {
          if (pendingDelete !== null) pool.remove(pendingDelete);
          setPendingDelete(null);
        }}
        onCancel={() => {
          setPendingDelete(null);
        }}
      />
    </fieldset>
  );
}
